import { ref, computed } from 'vue'
import { useChatSocket } from './useChatSocket'

/**
 * Moderation state + actions.
 *
 * Two concerns in one composable:
 *  1. The current user's own-mute state (driven by `moderation.muted` frames
 *     from the server). Drives the Composer's disabled/banner state.
 *  2. Admin actions: delete a message, mute/unmute a user. Called from the
 *     per-message kebab and the AdminMuteDialog.
 *
 * The own-mute listener is wired on first use; actions are stateless.
 */

const myMute = ref<{ until: string | null; reason: string | null } | null>(null)
let wired = false

function wire() {
    if (wired) return
    wired = true
    const { on } = useChatSocket()

    on('moderation.muted', (frame) => {
        if (frame?.unmuted) {
            myMute.value = null
            return
        }
        myMute.value = {
            until: frame?.until || null,
            reason: frame?.reason || null
        }
    })

    // Clear mute on disconnect so stale banner doesn't linger if admin
    // unmutes during the gap — client will receive the updated state on
    // the next connect via the WS-open snapshot anyway.
    on('__close', () => {
        // keep whatever we had — re-sync from WS-open handles it
    })
}

/** Whether the current user's mute is still active right now. Accounts for
 *  `until` being in the past but the WS frame not yet received. */
function isMuted(): boolean {
    if (!myMute.value) return false
    const { until } = myMute.value
    if (!until) return true // indefinite
    return new Date(until).getTime() > Date.now()
}

async function deleteMessage(messageId: string): Promise<boolean> {
    try {
        await $fetch(`/api/chat/messages/${messageId}`, { method: 'DELETE' })
        return true
    } catch (err) {
        console.error('[chat:mod] delete failed', err)
        return false
    }
}

async function muteUser(userId: string, ttlMinutes: number | null, reason: string | null): Promise<boolean> {
    try {
        await $fetch('/api/chat/admin/mute', {
            method: 'POST',
            body: { userId, ttlMinutes, reason }
        })
        return true
    } catch (err) {
        console.error('[chat:mod] mute failed', err)
        return false
    }
}

async function unmuteUser(userId: string): Promise<boolean> {
    try {
        await $fetch(`/api/chat/admin/mute/${userId}`, { method: 'DELETE' })
        return true
    } catch (err) {
        console.error('[chat:mod] unmute failed', err)
        return false
    }
}

export function useChatModeration() {
    wire()

    return {
        myMute: computed(() => myMute.value),
        isMuted,
        deleteMessage,
        muteUser,
        unmuteUser,
    }
}
